import { ROUTES } from "@/constants/routes";
import AddCampaignJobBoardPage from "@/features/campaigns/jobboards/pages/AddCampaignJobBoardPage";
import CampaignJobBoardDetailPage from "@/features/campaigns/jobboards/pages/CampaignJobBoardDetailPage";
import EditCampaignJobBoardPage from "@/features/campaigns/jobboards/pages/EditCampaignJobBoardPage";
import AddCampaignPage from "@/features/campaigns/pages/AddCampaignPage";
import CampaignDetailPage from "@/features/campaigns/pages/CampaignDetailPage";
import CampaignsPage from "@/features/campaigns/pages/CampaignsPage";
import EditCampaignPage from "@/features/campaigns/pages/EditCampaignPage";
import CaptainBillingPage from "@/features/captain-billing/pages/CaptainBillingPage";
import { HomePage } from "@/features/home";
import { InsightsPage } from "@/features/insights";
import { integrationsRoutes } from "@/features/integrations/integration-routes";
import ApplyDetailPage from "@/features/integrations/modules/applies/pages/detail/ApplyDetailPage";
import AppliesPage from "@/features/integrations/modules/applies/pages/overview/AppliesPage";
import CandidatesPage from "@/features/integrations/modules/candidates/pages/overview/CandidatesPage";
import GeneralReportingPage from "@/features/integrations/modules/reporting/pages/GeneralReporting/GeneralReportingPage";
import { notificationSettingsRoute } from "@/features/notifications/notification-settings-routes";
import NotificationsPage from "@/features/notifications/pages/NotificationsPage";
import { SEGMENT_MODE } from "@/features/segments/builder/constants";
import { SegmentBuilderPage } from "@/features/segments/builder/pages/SegmentBuilderPage";
import OverviewSegmentsPage from "@/features/segments/pages/overview/OverviewSegmentsPage";
import AddUserPage from "@/features/users/pages/add/AddUserPage";
import EditUserPage from "@/features/users/pages/edit/EditUserPage";
import OverviewUsersPage from "@/features/users/pages/overview/OverviewUsersPage";
import OverviewVacanciesPage from "@/features/vacancies/pages/overview/OverviewVacanciesPage";
import WebAnalyticsPage from "@/features/web-analytics/pages/WebAnalyticsPage";
import { Action } from "@/lib/hooks/usePermissionMatrix";
import VacancyDetailPage from "@/pages/vacancies/details/VacancyDetailPage";
import { RouteConfig } from "@/types/route-types";
// Client routes configuration (available to both admins and clients)
export const getClientRoutes = (): RouteConfig[] =>
  [
    {
      path: ROUTES.home,
      element: HomePage,
      routeConfigurations: {
        excludeInnerLayout: true,
        title: ({ t }) => t("routes.client.home"),
        breadcrumb: ({ t }) => ({
          currentPage: { label: t("routes.client.home") },
        }),
      },
    },
    {
      path: ROUTES.campaigns,
      routeConfigurations: {
        title: ({ t }) => t("common.campaigns"),
        breadcrumb: ({ t }) => ({
          currentPage: { label: t("common.campaigns") },
        }),
        excludeInnerLayout: true,
      },
      children: [
        {
          index: true,
          element: CampaignsPage,
          routeConfigurations: {
            excludeInnerLayout: true,
          },
        },
        {
          path: ROUTES.new,
          element: AddCampaignPage,
          routeConfigurations: {
            title: ({ t }) => t("admin.campaigns.add.title"),
            breadcrumb: ({ t }) => ({
              currentPage: { label: t("common.add") },
              navigation: [
                {
                  label: t("common.campaigns"),
                  url: `/${ROUTES.campaigns}`,
                },
              ],
            }),
          },
        },
        {
          path: ":campaignId",
          children: [
            { index: true, element: CampaignDetailPage },
            { path: ROUTES.edit, element: EditCampaignPage },
            {
              path: ROUTES.campaignIntegrations,
              children: [
                {
                  path: ROUTES.new,
                  element: AddCampaignJobBoardPage,
                },
                {
                  path: ":clientIntegrationModuleId",
                  children: [
                    {
                      index: true,
                      element: CampaignJobBoardDetailPage,
                    },
                    {
                      path: ROUTES.edit,
                      element: EditCampaignJobBoardPage,
                    },
                  ],
                },
              ],
            },
          ],
        },
      ],
    },
    {
      path: ROUTES.segments,
      routeConfigurations: {
        title: ({ t }) => t("common.segments"),
        breadcrumb: ({ t }) => ({
          currentPage: { label: t("common.segments") },
        }),
      },
      children: [
        { index: true, element: OverviewSegmentsPage },
        {
          path: ROUTES.new,
          element: <SegmentBuilderPage mode={SEGMENT_MODE.EDIT} />,
          routeConfigurations: {
            excludeInnerLayout: true,
            title: ({ t }) => t("admin.segments.create"),
            breadcrumb: ({ t }) => ({
              currentPage: { label: t("admin.segments.create") },
              navigation: [
                { label: t("common.segments"), url: `/${ROUTES.segments}` },
              ],
            }),
          },
          permission: {
            entity: "segments",
            action: Action.CREATE,
            fallbackPath: `${ROUTES.segments}`,
          },
        },
        {
          path: ":segmentId",
          routeConfigurations: {
            excludeInnerLayout: true,
          },
          children: [
            {
              index: true,
              element: <SegmentBuilderPage mode={SEGMENT_MODE.VIEW} />,
              permission: {
                entity: "segments",
                action: Action.VIEW,
                fallbackPath: `${ROUTES.segments}`,
              },
              routeConfigurations: {
                title: ({ t }) => t("common.viewSegment"),
                breadcrumb: ({ t }) => ({
                  currentPage: { label: t("common.viewSegment") },
                  navigation: [
                    { label: t("common.segments"), url: `/${ROUTES.segments}` },
                  ],
                }),
              },
            },
            {
              path: ROUTES.edit,
              element: <SegmentBuilderPage mode={SEGMENT_MODE.EDIT} />,
              permission: {
                entity: "segments",
                action: Action.EDIT,
                fallbackPath: `${ROUTES.segments}`,
              },
              routeConfigurations: {
                title: ({ t }) => t("common.editSegment"),
                breadcrumb: ({ t }) => ({
                  currentPage: { label: t("common.editSegment") },
                  navigation: [
                    { label: t("common.segments"), url: `/${ROUTES.segments}` },
                  ],
                }),
              },
            },
          ],
        },
      ],
    },

    {
      path: ROUTES.vacancies,
      routeConfigurations: {
        title: ({ t }) => t("common.vacancies"),
        breadcrumb: ({ t }) => ({
          currentPage: { label: t("common.vacancies") },
        }),
      },
      children: [
        { index: true, element: OverviewVacanciesPage },
        {
          path: ":vacancyId",
          element: VacancyDetailPage,
        },
      ],
    },
    {
      path: ROUTES.insights,
      element: <InsightsPage />,
    },
    {
      // Optional :level? segment so /reporting (default Vacancy), /reporting/campaign and
      // /reporting/integration all resolve to the same page; the page reads the level from
      // useParams and dispatches the hooks accordingly.
      path: `${ROUTES.reporting}/:level?`,
      element: GeneralReportingPage,
      routeConfigurations: {
        excludeInnerLayout: true,
        title: ({ t }) => t("integrations.modules.names.reporting", "Reporting"),
        breadcrumb: ({ t }) => ({
          currentPage: { label: t("integrations.modules.names.reporting", "Reporting") },
        }),
      },
    },
    {
      path: ROUTES.applies,
      routeConfigurations: {
        excludeInnerLayout: true,
        title: ({ t }) => t("common.applies"),
        breadcrumb: ({ t }) => ({
          currentPage: { label: t("common.applies") },
        }),
      },
      children: [
        { index: true, element: AppliesPage },
        // Static "candidates" outranks the sibling ":applyId" in React-Router v6 ranking, so
        // /applies/candidates resolves here while apply-id GUIDs still hit the detail page (OR-2155).
        { path: ROUTES.candidates, element: CandidatesPage },
        { path: ":applyId", element: ApplyDetailPage },
      ],
    },
    {
      path: ROUTES.webAnalytics,
      element: WebAnalyticsPage,
      routeConfigurations: {
        excludeInnerLayout: true,
        title: ({ t }) => t("webAnalytics.title"),
        breadcrumb: ({ t }) => ({
          currentPage: { label: t("webAnalytics.title") },
        }),
      },
    },
    {
      path: ROUTES.tokenManagement,
      element: CaptainBillingPage,
      permission: {
        entity: "captainBilling",
        action: Action.VIEW,
        fallbackPath: `${ROUTES.home}`,
      },
      routeConfigurations: {
        excludeInnerLayout: true,
        title: ({ t }) => t("captainBilling.title"),
      },
    },
    {
      path: ROUTES.users,
      routeConfigurations: {
        excludeInnerLayout: true,
        title: ({ t }) => t("client.userManagement.title"),
        breadcrumb: ({ t }) => ({
          currentPage: { label: t("client.userManagement.title") },
        }),
      },

      children: [
        {
          index: true,
          element: OverviewUsersPage,
        },
        {
          path: ROUTES.new,
          element: AddUserPage,
          permission: {
            entity: "users",
            action: Action.CREATE,
            fallbackPath: `${ROUTES.users}`,
          },
          routeConfigurations: {
            title: ({ t }) => t("client.userManagement.addUser.title"),
            breadcrumb: ({ t }) => ({
              currentPage: { label: t("client.userManagement.addUser.title") },
              navigation: [
                {
                  label: t("client.userManagement.title"),
                  url: `/${ROUTES.users}`,
                },
              ],
            }),
          },
        },
        {
          path: ":userId",
          children: [
            {
              index: true,
              element: EditUserPage,
              permission: {
                entity: "users",
                action: Action.EDIT,
                fallbackPath: `${ROUTES.users}`,
              },
              routeConfigurations: {
                title: ({ t }) => t("client.userManagement.editUser.title"),
                breadcrumb: ({ t }) => ({
                  currentPage: {
                    label: t("client.userManagement.editUser.title"),
                  },
                  navigation: [
                    {
                      label: t("client.userManagement.title"),
                      url: `/${ROUTES.users}`,
                    },
                  ],
                }),
              },
            },
            {
              path: ROUTES.edit,
              element: EditUserPage,
              permission: {
                entity: "users",
                action: Action.EDIT,
                fallbackPath: `${ROUTES.users}`,
              },
              routeConfigurations: {
                title: ({ t }) => t("client.userManagement.editUser.title"),
                breadcrumb: ({ t }) => ({
                  currentPage: {
                    label: t("client.userManagement.editUser.title"),
                  },
                  navigation: [
                    {
                      label: t("client.userManagement.title"),
                      url: `/${ROUTES.users}`,
                    },
                  ],
                }),
              },
            },
          ],
        },
      ],
    },
    {
      path: ROUTES.notifications,
      element: NotificationsPage,
      routeConfigurations: {
        excludeInnerLayout: true,
        title: ({ t }) => t("notifications.title"),
        breadcrumb: ({ t }) => ({
          currentPage: { label: t("notifications.title") },
        }),
      },
    },
    notificationSettingsRoute,
    integrationsRoutes,
  ] as const;
